import Icon, { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useMenu } from "@desk/accessControlProvider";
import { css, cx } from "@emotion/css";
import { useThemedLayoutContext } from "@refinedev/antd";
import { useNotification, useTranslate } from "@refinedev/core";
import type { TreeMenuItem } from "@refinedev/core/dist/hooks/menu/useMenu";
import type { MenuProps } from "antd";
import { Button, Drawer, Grid, Layout, Menu, Skeleton, theme, Typography } from "antd";
import { find, flatMapDeep, last } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";

import { useGlobalStore } from "@/store";

import CollapseIcon from "./assets/collapse.svg?react";
import RefreshIcon from "./assets/refresh.svg?react";

const { useToken } = theme;

const { Text } = Typography;
type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  meta?: any,
  type?: "group"
): MenuItem {
  return {
    key,
    icon,
    children,
    label: key === "bizkey" ? label + "(Beta)" : label,
    meta,
    type,
  } as MenuItem;
}

export const Sider: React.FC<any> = ({ Title = "Pabbit Admin", icon, fixed, onRefresh, refreshState, children }) => {
  const translate = useTranslate();
  const { propertyId } = useGlobalStore();
  const { menuItems, isLoading, selectedKey, defaultOpenKeys } = useMenu(propertyId);
  const breakpoint = Grid.useBreakpoint();
  const { token } = useToken();
  const { open } = useNotification();
  const { siderCollapsed, setSiderCollapsed, mobileSiderOpen, setMobileSiderOpen } = useThemedLayoutContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [currentSelectdKey, setCurrentSelectdKey] = useState<string>("");

  const flattedMenuItems = useMemo(
    () => flatMapDeep(menuItems, (item) => (item?.name ? [item, ...item.children] : [item])),
    [menuItems]
  );

  useEffect(() => {
    if (defaultOpenKeys) {
      const lastKey = last(defaultOpenKeys);
      if (!lastKey) return;
      setOpenKeys([...defaultOpenKeys, lastKey?.replace("/", "")]);
    }
  }, [defaultOpenKeys]);
  useEffect(() => {
    if (selectedKey) {
      const active = find(flattedMenuItems, { key: selectedKey });
      if (active) {
        setCurrentSelectdKey(active.name);
      }
    }
  }, [selectedKey, flattedMenuItems]);

  const isMobile = typeof breakpoint.lg === "undefined" ? false : !breakpoint.lg;

  const generateItems = (menus: TreeMenuItem[]): MenuItem[] =>
    menus.map(({ name, icon, meta, children }) =>
      getItem(
        meta && meta.disabled ? (
          <Text disabled>{translate(`${name}.${name}`, meta.label ?? name)}</Text>
        ) : (
          translate(`${name}.${name}`, meta?.label ?? name)
        ),
        name,
        icon ?? meta?.icon,
        children.length ? generateItems(children) : undefined,
        meta
      )
    );
  const toggleSider = () => {
    if (isMobile) {
      setMobileSiderOpen(!mobileSiderOpen);
    } else {
      setSiderCollapsed(!siderCollapsed);
    }
  };

  const ThemeTitle = () => (
    <div className="flex max-h-16 w-full items-center" style={{ marginLeft: siderCollapsed ? 16 : 0 }}>
      {!siderCollapsed && (
        <div className="mr-12 hidden w-32 gap-2 text-white lg:flex">
          {icon}
          <div className="whitespace-nowrap text-base font-semibold">{Title}</div>
        </div>
      )}
      <div className="flex gap-6 text-2xl">
        <Icon className="cursor-pointer" component={CollapseIcon} onClick={toggleSider} />
        <Icon
          className={cx("cursor-pointer", { "animate-spin": refreshState })}
          component={RefreshIcon}
          onClick={onRefresh}
        />
        {children}
      </div>
    </div>
  );

  const renderMenu = () => {
    return (
      <Skeleton loading={isLoading}>
        <Menu
          className={cx(css`
            .ant-menu-item:not(.ant-menu-item-selected) > svg,
            .ant-menu-submenu:not(.ant-menu-submenu-selected) svg {
              color: #909899 !important;
            }
          `)}
          defaultOpenKeys={defaultOpenKeys}
          items={generateItems(menuItems)}
          mode="inline"
          onClick={({ key }) => {
            const listPath = find(flattedMenuItems, { name: key })?.list as string;
            const match = matchPath(listPath, pathname);
            if (currentSelectdKey === key && !match) {
              navigate(listPath);
            }
          }}
          // openKeys={["/devices/PATMO", "/devices", "devices"]}
          onOpenChange={setOpenKeys}
          onSelect={({ key }) => {
            if (key === "devices") {
              const { children } = find(menuItems, { name: key }) ?? {};
              if (!children?.length) {
                open?.({
                  message: "デバイスが設置されていません。",
                  type: "error",
                  key: "notification-permission",
                });
                if (pathname.split("/").includes("devices-config")) {
                  navigate("/dashboard");
                }
                return;
              }
            }
            const { route, name, meta, list, key: menuKey } = find(flattedMenuItems, { name: key }) || {};
            if (meta && meta.disabled) {
              return open?.({
                message: meta.error ?? "操作権限がありません。",
                type: "error",
                key: "notification-permission",
              });
            } else if (list && name) {
              setCurrentSelectdKey(menuKey ?? name);
              navigate(list as string);
            }
          }}
          openKeys={openKeys}
          selectedKeys={[currentSelectdKey]}
          style={{
            paddingTop: "8px",
            border: "none",
            overflow: "auto",
            height: "calc(100% - 72px)",
          }}
        >
          {/* {renderSider()} */}
        </Menu>
      </Skeleton>
    );
  };

  const renderDrawerSider = () => {
    return (
      <>
        <div className="fixed left-0 top-0">
          <div
            style={{
              width: "200px",
              padding: "0 16px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              height: "64px",
              borderBottom: `1px solid ${token.colorBorder}`,
            }}
          >
            <ThemeTitle />
          </div>
        </div>
        <Drawer
          closable={false}
          maskClosable={true}
          onClose={() => setMobileSiderOpen(false)}
          open={mobileSiderOpen}
          placement="left"
          styles={{ body: { padding: 0 } }}
          width={200}
        >
          <Layout>
            <Layout.Sider
              style={{
                height: "100vh",
                backgroundColor: token.colorBgContainer,
                borderRight: `1px solid ${token.colorBgElevated}`,
              }}
            >
              {renderMenu()}
            </Layout.Sider>
          </Layout>
        </Drawer>
      </>
    );
  };

  if (isMobile) {
    return renderDrawerSider();
  }

  const siderStyles: React.CSSProperties = {
    backgroundColor: token.colorBgContainer,
    borderRight: `1px solid ${token.colorBgElevated}`,
  };

  if (fixed) {
    siderStyles.position = "fixed";
    siderStyles.top = 0;
    siderStyles.height = "100vh";
    siderStyles.zIndex = 999;
  }

  return (
    <>
      {fixed && (
        <div
          style={{
            width: siderCollapsed ? "80px" : "200px",
            transition: "all 0.2s",
          }}
        />
      )}
      <Layout.Sider
        breakpoint="lg"
        collapsed={siderCollapsed}
        collapsedWidth={0.001}
        collapsible
        onCollapse={(collapsed, type) => {
          if (type === "clickTrigger") {
            setSiderCollapsed(collapsed);
          }
        }}
        style={siderStyles}
        trigger={
          <Button
            style={{
              borderRadius: 0,
              height: "100%",
              width: "100%",
              backgroundColor: token.colorBgElevated,
            }}
            type="text"
          >
            {siderCollapsed ? (
              <RightOutlined
                style={{
                  color: token.colorPrimary,
                }}
              />
            ) : (
              <LeftOutlined
                style={{
                  color: token.colorPrimary,
                }}
              />
            )}
          </Button>
        }
      >
        <div
          style={{
            width: 200,
            padding: siderCollapsed ? "0" : "0 16px",
            display: "flex",
            justifyContent: siderCollapsed ? "center" : "flex-start",
            alignItems: "center",
            height: "64px",
            backgroundColor: token.colorPrimary,
            fontSize: "14px",
            // borderBottom: `1px solid ${token.colorBorder}`,
          }}
        >
          {/* <ThemedTitleV2 collapsed={siderCollapsed} text={Title} icon={icon} /> */}
          <ThemeTitle />
        </div>
        {renderMenu()}
      </Layout.Sider>
    </>
  );
};
